import React from "react";
import { Link } from "react-router-dom";
import { Header, Footer } from "../components/Layout";
import { ArrowLeft } from "@phosphor-icons/react";

const SECTION = "mt-8 first:mt-0";
const H2 = "font-serif-display text-2xl sm:text-3xl text-[#3F2D1B] mb-3 leading-tight";
const P = "text-sm sm:text-base text-[#3F2D1B] leading-relaxed mb-3";
const UL = "list-disc pl-6 space-y-1.5 mb-4 text-sm sm:text-base text-[#3F2D1B]";

export default function TermsOfServicePage() {
  return (
    <div className="min-h-[100dvh] bg-[#FAF4E8] flex flex-col">
      <Header />
      <main className="flex-1 max-w-3xl mx-auto w-full px-5 sm:px-8 py-10 sm:py-14">
        <Link to="/" className="inline-flex items-center gap-1 text-sm text-muted hover:text-[#3F2D1B] mb-6" data-testid="terms-back">
          <ArrowLeft size={14} /> Back to home
        </Link>

        <div className="text-[10px] uppercase tracking-[0.3em] text-amber-700 font-semibold mb-2">
          AstroKuberChat &middot; Legal
        </div>
        <h1 className="font-serif-display text-4xl sm:text-5xl text-[#3F2D1B] leading-tight">
          Terms of Service
        </h1>
        <p className="text-xs text-muted mt-3">
          Effective date: 26 February 2026 &middot; Last updated: 26 February 2026
        </p>

        <div className="mt-10 space-y-1" data-testid="terms-content">

          {/* ── 1. Acceptance ─────────────────────────────────── */}
          <section className={SECTION}>
            <h2 className={H2}>1. Acceptance of Terms</h2>
            <p className={P}>
              These Terms of Service (&ldquo;Terms&rdquo;) govern your use of
              AstroKuberChat (the &ldquo;Service&rdquo;) accessible at
              <b> https://astrokuberchat.com</b>, as the <b>AstroKuberChat</b>{" "}
              Android app on Google Play (package{" "}
              <code>com.astrokuberchat.twa</code>), and as the <b>AstroKuberChat</b>{" "}
              iOS app on the Apple App Store. By creating an account,
              chatting with an astrologer, using any calculator or making a
              payment, you agree to be bound by these Terms and by our{" "}
              <Link to="/privacy" className="text-amber-700 underline">Privacy Policy</Link>.
              If you do not agree, please do not use the Service.
            </p>
          </section>

          {/* ── 2. Eligibility ─────────────────────────────────── */}
          <section className={SECTION}>
            <h2 className={H2}>2. Eligibility</h2>
            <p className={P}>
              You must be at least <b>18 years old</b> and capable of entering
              into a legally binding contract under the Indian Contract Act,
              1872 (or equivalent law of your jurisdiction). By using the
              Service you represent that you meet these requirements.
            </p>
          </section>

          {/* ── 3. Entertainment-only disclaimer ───────────────── */}
          <section className={SECTION}>
            <h2 className={H2}>3. Nature of the Service &middot; Entertainment Only</h2>
            <p className={P}>
              AstroKuberChat provides astrological readings, calculations,
              interpretations and consultations <b>for entertainment,
              self-reflection and cultural purposes only</b>. Astrology is NOT
              a science. Our content is <u>not</u> a substitute for
              professional medical, legal, financial, psychological, or other
              licensed advice. Decisions you take based on our content are
              your own responsibility.
            </p>
            <p className={P}>
              You should always consult a qualified professional before taking
              any decision that materially affects your health, wealth,
              relationships, business or legal standing.
            </p>
          </section>

          {/* ── 4. Account ─────────────────────────────────────── */}
          <section className={SECTION}>
            <h2 className={H2}>4. Your account</h2>
            <ul className={UL}>
              <li>You sign in using Google Sign-In. You are responsible for
                safeguarding your Google account credentials.</li>
              <li>You agree to provide accurate, current information
                (including birth details if you want accurate readings).</li>
              <li><b>One person, one account.</b> Multiple accounts to abuse
                promotional credits, first-chat cashback, anniversary cashback
                or any other offer will lead to forfeiture and ban.</li>
              <li>You may permanently delete your account and all associated
                data at any time via{" "}
                <Link to="/account-deletion" className="text-amber-700 underline">
                  astrokuberchat.com/account-deletion
                </Link>{" "}
                (instant for signed-in users; within 7 days for email
                requests).</li>
            </ul>
          </section>

          {/* ── 5. Astrologers (human + AI) ────────────────────── */}
          <section className={SECTION}>
            <h2 className={H2}>5. Human and AI astrologers</h2>
            <p className={P}>
              The Service offers two kinds of consultations, each clearly
              labelled in the astrologer list:
            </p>
            <ul className={UL}>
              <li><b>Human astrologers</b> are independent practitioners, not
                our employees. We onboard each through a verification process
                but do not warrant the accuracy, completeness or quality of
                any individual reading.</li>
              <li><b>AI astrologers</b> are powered by large language models
                (Anthropic Claude, Google Gemini) constrained by your
                Swiss-Ephemeris-computed chart points and the Parashari /
                Jaimini / KP / Nadi system specified on that astrologer&rsquo;s
                profile. AI readings are generated programmatically and may
                occasionally contain errors.</li>
            </ul>
            <p className={P}>
              Treat all astrologers with courtesy. Abusive behaviour towards
              human astrologers or attempts to elicit harmful, sexual or
              illegal content from AI astrologers will result in suspension of
              your account.
            </p>
          </section>

          {/* ── 6. Pricing & wallet ────────────────────────────── */}
          <section className={SECTION}>
            <h2 className={H2}>6. Wallet, payments and pricing</h2>
            <ul className={UL}>
              <li><b>Per-minute chat billing</b> &mdash; chat with astrologers
                is billed per minute at the rate displayed on each
                astrologer&rsquo;s profile (currently &#8377;6&ndash;&#8377;55/min
                or USD equivalent).</li>
              <li><b>Voice (PSTN) calls</b> &mdash; billed per minute at the rate
                shown on the astrologer&rsquo;s profile. Calls are routed via
                Twilio; we cover the carrier cost out of the per-minute rate.</li>
              <li><b>Premium tools</b> &mdash; one-off paid unlocks (e.g. 20-Year
                Forecast, Life Replay, Kalachakra Dasha). The exact price is
                shown before checkout.</li>
              <li><b>VIP King Pack</b> &mdash; a paid bundle that unlocks all
                premium tools for the duration shown on the upsell page.</li>
              <li><b>Currency</b> &mdash; auto-detected from your location. Indian
                users see prices in &#8377; (Razorpay). All other users see
                prices in USD (PayPal). You can switch manually from the
                wallet page.</li>
              <li><b>Taxes</b> &mdash; <b>18% GST is added on top</b> of the
                advertised recharge amount for users billed in INR, as required
                by Indian tax law. International users see the equivalent
                regional tax (where applicable) added at checkout. Promotional
                bonuses (auto pack-bonus, coupon cashback) are NOT taxed.</li>
              <li><b>Promotional credit</b> &mdash; new users receive a complimentary
                wallet credit on first sign-up. Active campaigns may include
                a 100% first-chat cashback and an Anniversary 50% Cashback
                (capped at &#8377;5,000) on subsequent recharges. All offers
                are subject to anti-abuse checks; multi-account abuse forfeits
                the credit.</li>
              <li><b>Payment processors</b> &mdash; wallet recharges are processed
                by <b>Razorpay</b> (INR) and <b>PayPal</b> (USD). By recharging,
                you also accept the respective processor&rsquo;s terms of
                service.</li>
              <li><b>Stranded payments</b> &mdash; if a payment is debited from your
                account but does not reflect in your wallet within 30 minutes,
                email us with the gateway transaction ID and we will reconcile
                or refund within 7 business days.</li>
              <li><b>Wallet credits</b> are <b>non-transferable</b> and have
                no cash value outside the Service.</li>
            </ul>
          </section>

          {/* ── 7. Refunds ─────────────────────────────────────── */}
          <section className={SECTION}>
            <h2 className={H2}>7. Refund policy</h2>
            <p className={P}>
              <b>Successfully credited wallet balances are non-refundable</b>{" "}
              once the recharge has been processed and added to your wallet,
              because the value is immediately available for consumption.
            </p>
            <p className={P}>
              However, we will gladly investigate and, at our sole discretion,
              refund a chat, call or paid service if:
            </p>
            <ul className={UL}>
              <li>The astrologer was unavailable or the chat / call dropped
                due to a Service-side technical fault.</li>
              <li>The session was billed in error.</li>
              <li>A duplicate payment was charged for the same booking.</li>
              <li>A premium tool failed to generate after a successful
                purchase.</li>
            </ul>
            <p className={P}>
              Email{" "}
              <a className="text-amber-700 underline" href="mailto:support@astrokuberchat.com">support@astrokuberchat.com</a>{" "}
              within <b>7 days</b> of the disputed transaction with your
              transaction ID and a brief description. Approved refunds are
              processed via the original payment method within 7&ndash;10
              business days.
            </p>
          </section>

          {/* ── 8. User conduct ────────────────────────────────── */}
          <section className={SECTION}>
            <h2 className={H2}>8. User conduct</h2>
            <p className={P}>You agree NOT to:</p>
            <ul className={UL}>
              <li>Use the Service for any unlawful, abusive, threatening,
                sexually explicit, hateful or harassing purpose.</li>
              <li>Impersonate any other person or misrepresent your
                identity.</li>
              <li>Reverse-engineer, scrape, copy or resell any part of the
                Service without our written consent.</li>
              <li>Attempt to bypass paywalls, share paid content with others,
                or exploit promotional credits in bulk.</li>
              <li>Upload malicious code or attempt to disrupt the Service.</li>
              <li>Attempt to elicit content from AI astrologers that violates
                Google Play policies, Anthropic / Google usage policies or
                Indian law.</li>
            </ul>
            <p className={P}>
              Violation may result in immediate suspension or termination of
              your account, forfeiture of wallet balance, and legal action
              where applicable.
            </p>
          </section>

          {/* ── 9. IP ──────────────────────────────────────────── */}
          <section className={SECTION}>
            <h2 className={H2}>9. Intellectual Property</h2>
            <p className={P}>
              All content on the Service &mdash; the calculator engines,
              written interpretations, celebrity database entries, learning
              material, UI, AstroKuberChat name and logo &mdash; is the
              property of AstroKuberChat (or licensed to us) and protected by
              Indian and international IP laws. You may view content for
              personal, non-commercial use only. You may not republish, copy
              or redistribute without prior written consent.
            </p>
            <p className={P}>
              Birth details and chat content that you submit remain your
              property; you grant us a limited, royalty-free licence to
              process them solely to provide the Service.
            </p>
          </section>

          {/* ── 10. Liability ──────────────────────────────────── */}
          <section className={SECTION}>
            <h2 className={H2}>10. Limitation of liability</h2>
            <p className={P}>
              To the maximum extent permitted by applicable law:
            </p>
            <ul className={UL}>
              <li>The Service is provided <b>&ldquo;as is&rdquo;</b> and
                <b> &ldquo;as available&rdquo;</b> without warranties of any kind.</li>
              <li>We are not liable for indirect, incidental, consequential,
                punitive or special damages arising out of your use of the
                Service.</li>
              <li>Our total aggregate liability for any claim arising out of
                or relating to the Service is limited to the amount you paid
                us in the 3 months preceding the event giving rise to the
                claim, or &#8377;1,000 (USD 12), whichever is lower.</li>
            </ul>
          </section>

          {/* ── 11. Suspension ─────────────────────────────────── */}
          <section className={SECTION}>
            <h2 className={H2}>11. Suspension &amp; termination</h2>
            <p className={P}>
              We may suspend or terminate your account at our discretion if
              you breach these Terms, abuse the Service, or for prolonged
              inactivity (more than 24 months). Upon termination, any
              remaining wallet balance acquired via promotional credits is
              forfeited; balance acquired via paid recharge may be refunded
              at our discretion.
            </p>
          </section>

          {/* ── 12. Google Play specifics ──────────────────────── */}
          <section className={SECTION}>
            <h2 className={H2}>12. Android app (Google Play) specifics</h2>
            <p className={P}>
              The AstroKuberChat Android app is distributed via Google Play
              and is a Trusted Web Activity (TWA) wrapper around our website.
              By installing or using the Android app, you additionally agree
              to:
            </p>
            <ul className={UL}>
              <li><b>Google Play&rsquo;s Terms of Service</b> for Android app
                distribution.</li>
              <li><b>Google Play&rsquo;s User Data Policy and Developer
                Programme Policies</b>. Any conflict between these Terms and
                Google Play&rsquo;s policies is resolved in favour of
                Google&rsquo;s policies for the Android-app experience.</li>
              <li>Payments inside the Android app are processed by the same
                gateways (Razorpay/PayPal) via embedded web checkout, not via
                Google Play Billing. Google Play Billing is therefore not
                involved in wallet recharges.</li>
            </ul>
          </section>

          {/* ── 12b. Apple App Store specifics + EULA boilerplate ─ */}
          <section className={SECTION}>
            <h2 className={H2}>12b. iOS app (Apple App Store) specifics</h2>
            <p className={P}>
              The AstroKuberChat iOS app is distributed via the Apple App Store
              and is a Capacitor WebView wrapper around our website. These
              Terms constitute the End User Licence Agreement (&ldquo;EULA&rdquo;)
              between <b>you</b> and <b>AstroKuberChat</b> (the developer) for
              the iOS app. Apple Inc. is <b>not</b> a party to this EULA and
              is not responsible for the app or its content. In particular:
            </p>
            <ul className={UL}>
              <li><b>Acknowledgement.</b> This EULA is concluded between
                AstroKuberChat and you only, and not with Apple. AstroKuberChat
                (not Apple) is solely responsible for the iOS app and its
                content.</li>
              <li><b>Scope of licence.</b> The licence granted to you is
                limited to a non-transferable licence to use the iOS app on
                any Apple-branded device that you own or control, and as
                permitted by the &ldquo;Usage Rules&rdquo; set forth in the
                Apple Media Services Terms and Conditions, except that the
                iOS app may be accessed and used by other accounts associated
                with the purchaser via Family Sharing or volume purchasing.</li>
              <li><b>Maintenance and support.</b> AstroKuberChat is solely
                responsible for providing any maintenance and support services
                with respect to the iOS app. Apple has no obligation whatsoever
                to furnish any maintenance and support services with respect to
                the iOS app.</li>
              <li><b>Warranty.</b> AstroKuberChat is solely responsible for any
                product warranties, whether express or implied by law, to the
                extent not effectively disclaimed. In the event of any failure
                of the iOS app to conform to any applicable warranty, you may
                notify Apple, and Apple will refund the purchase price for the
                iOS app to you (if any); and to the maximum extent permitted by
                applicable law, Apple will have no other warranty obligation
                whatsoever with respect to the iOS app, and any other claims,
                losses, liabilities, damages, costs or expenses attributable to
                any failure to conform to any warranty will be
                AstroKuberChat&rsquo;s sole responsibility.</li>
              <li><b>Product claims.</b> AstroKuberChat, not Apple, is
                responsible for addressing any claims by you or any third
                party relating to the iOS app or your possession and/or use of
                the iOS app, including but not limited to (i) product
                liability claims; (ii) any claim that the iOS app fails to
                conform to any applicable legal or regulatory requirement; and
                (iii) claims arising under consumer protection, privacy, or
                similar legislation.</li>
              <li><b>Intellectual property rights.</b> In the event of any
                third-party claim that the iOS app or your possession and use
                of the iOS app infringes that third party&rsquo;s intellectual
                property rights, AstroKuberChat (not Apple) will be solely
                responsible for the investigation, defence, settlement and
                discharge of any such intellectual property infringement
                claim.</li>
              <li><b>Legal compliance.</b> You represent and warrant that
                (i) you are not located in a country that is subject to a U.S.
                Government embargo, or that has been designated by the U.S.
                Government as a &ldquo;terrorist supporting&rdquo; country;
                and (ii) you are not listed on any U.S. Government list of
                prohibited or restricted parties.</li>
              <li><b>Developer contact information.</b> Direct any questions,
                complaints or claims regarding the iOS app to{" "}
                <a className="text-amber-700 underline" href="mailto:support@astrokuberchat.com">support@astrokuberchat.com</a>.</li>
              <li><b>Third-party terms of agreement.</b> You must comply with
                applicable third-party terms of agreement (e.g. your wireless
                data services agreement) when using the iOS app.</li>
              <li><b>Third-party beneficiary.</b> You acknowledge and agree
                that <b>Apple, and Apple&rsquo;s subsidiaries, are third-party
                beneficiaries of this EULA</b>, and that, upon your acceptance
                of the terms and conditions of this EULA, Apple will have the
                right (and will be deemed to have accepted the right) to
                enforce this EULA against you as a third-party beneficiary
                thereof.</li>
              <li><b>Person-to-Person Services and payments.</b> The iOS app
                facilitates consultations, per-minute chat, voice calls and
                downloadable reports provided by <b>third-party independent
                astrologers</b> (real-world services delivered outside the
                app). Under Apple App Store Review Guideline <b>3.1.3(e)</b>{" "}
                (Person-to-Person Services), payments for such services are
                processed through third-party gateways (Razorpay in INR,
                PayPal in USD) and Apple In-App Purchase is not required. If
                a future version of the app introduces digital content
                consumed entirely within the app (e.g. an auto-renewable
                subscription for premium in-app content), we will migrate that
                specific SKU to Apple In-App Purchase in accordance with the
                Guidelines.</li>
            </ul>
          </section>

          {/* ── 13. Law & jurisdiction ─────────────────────────── */}
          <section className={SECTION}>
            <h2 className={H2}>13. Governing law and jurisdiction</h2>
            <p className={P}>
              These Terms are governed by the laws of India. Any dispute
              arising out of or relating to these Terms or the Service shall
              be subject to the exclusive jurisdiction of the competent
              courts in <b>Bengaluru, Karnataka, India</b>.
            </p>
          </section>

          {/* ── 14. Changes ────────────────────────────────────── */}
          <section className={SECTION}>
            <h2 className={H2}>14. Changes to these Terms</h2>
            <p className={P}>
              We may update these Terms from time to time. Material changes
              will be announced in-app or via email at least 7 days before
              they take effect. The &ldquo;Last updated&rdquo; date at the top
              of this page will always reflect the most recent revision.
              Continued use of the Service after that date implies acceptance
              of the updated Terms.
            </p>
          </section>

          {/* ── 15. Contact ────────────────────────────────────── */}
          <section className={SECTION}>
            <h2 className={H2}>15. Contact</h2>
            <p className={P}>
              For any question about these Terms, write to:<br />
              <a className="text-amber-700 underline" href="mailto:support@astrokuberchat.com">support@astrokuberchat.com</a><br />
              Privacy:{" "}
              <a className="text-amber-700 underline" href="mailto:privacy@astrokuberchat.com">privacy@astrokuberchat.com</a><br />
              Account deletion:{" "}
              <Link to="/account-deletion" className="text-amber-700 underline">
                astrokuberchat.com/account-deletion
              </Link>
            </p>
          </section>

        </div>
      </main>
      <Footer />
    </div>
  );
}
